import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getTPRMAssessmentVendorEntryApi,
  getVendorAssessmentInvitationApi,
  saveTPRMAssessmentVendorEntryApi,
  saveVendorAssessmentResponsesApi,
  submitTPRMAssessmentApi,
  submitVendorAssessmentApi,
  uploadTPRMAssessmentEvidenceApi,
  uploadVendorAssessmentEvidenceApi,
} from "../../../services/axios/AuthApis/TPRMApis";

const inputClass =
  "mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:disabled:bg-slate-800";

const errorMessage = (error) => {
  const data = error?.response?.data;
  const value = data?.detail || data?.responses || error?.message;
  if (Array.isArray(value)) return value.join(" ");
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    return Object.values(value).flat().join(" ");
  }
  return "The assessment request failed. Please retry.";
};

const formatDate = (value) => {
  if (!value) return "Not specified";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not specified"
    : new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(date);
};

const questionIsApplicable = (question, answers) => {
  const condition = question.show_if;
  if (!condition) return true;
  const source = String(answers[condition.question_id] || "").trim().toLowerCase();
  const expected = String(condition.equals ?? "").trim().toLowerCase();
  if (!source || !expected) return false;
  if (source.includes(",")) {
    return source.split(",").map((item) => item.trim()).includes(expected);
  }
  return source === expected;
};

const getApplicableQuestions = (questions, answers) => {
  const applicable = [];
  const applicableIds = new Set();
  for (const question of questions) {
    const condition = question.show_if;
    if (
      condition
      && (
        !applicableIds.has(condition.question_id)
        || !questionIsApplicable(question, answers)
      )
    ) {
      continue;
    }
    applicable.push(question);
    if (question.question_id) applicableIds.add(question.question_id);
  }
  return applicable;
};

const VendorAssessmentPage = ({ assessmentId, onBack }) => {
  const staffEntry = Boolean(assessmentId);
  const [token, setToken] = useState("");
  const [invitation, setInvitation] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingQuestion, setUploadingQuestion] = useState("");
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (staffEntry) return;
    const fragmentToken = window.location.hash.slice(1);
    if (fragmentToken) {
      setToken(fragmentToken);
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.search}`,
      );
    } else {
      setError("This invitation link is missing its secure access token.");
      setLoading(false);
    }
  }, [staffEntry]);

  const loadInvitation = useCallback(async () => {
    if (!staffEntry && !token) return;
    setLoading(true);
    setError("");
    try {
      const result = staffEntry
        ? await getTPRMAssessmentVendorEntryApi(assessmentId)
        : await getVendorAssessmentInvitationApi(token);
      setInvitation(result);
      setAnswers(
        Object.fromEntries(
          (result.responses || []).map((item) => [
            item.question_id,
            item.response || "",
          ]),
        ),
      );
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [assessmentId, staffEntry, token]);

  useEffect(() => {
    if (staffEntry || token) loadInvitation();
  }, [staffEntry, token, loadInvitation, reloadKey]);

  const questionEvidence = useMemo(() => {
    const byQuestion = new Map();
    for (const item of invitation?.evidence || []) {
      if (!byQuestion.has(item.question_id)) byQuestion.set(item.question_id, []);
      byQuestion.get(item.question_id).push(item);
    }
    return byQuestion;
  }, [invitation]);

  const applicableQuestions = useMemo(
    () => getApplicableQuestions(
      invitation?.questions || [],
      answers,
    ),
    [invitation, answers],
  );
  const requiredQuestions = applicableQuestions.filter((question) => question.required);
  const completedRequiredQuestions = requiredQuestions.filter((question) => (
    String(answers[question.question_id] || "").trim()
      && (
        !question.evidence_required
        || (questionEvidence.get(question.question_id) || []).some(
          (item) =>
            item.verification_status === "Pending Review"
            || item.verification_status === "Verified",
        )
      )
  ));
  const completedRequiredQuestionIds = new Set(
    completedRequiredQuestions.map((question) => question.question_id),
  );
  const unansweredRequiredQuestions = requiredQuestions.filter(
    (question) => !completedRequiredQuestionIds.has(question.question_id),
  );
  const completionPercentage = requiredQuestions.length
    ? Math.round((completedRequiredQuestions.length / requiredQuestions.length) * 100)
    : 100;

  const updateAnswer = (questionId, value) => {
    setAnswers((current) => ({ ...current, [questionId]: value }));
    setDirty(true);
    setFeedback("");
  };

  const responsePayload = () => {
    const applicableIds = new Set(
      applicableQuestions.map((question) => question.question_id),
    );
    return (invitation?.questions || []).map((question) => ({
      question_id: question.question_id,
      response: applicableIds.has(question.question_id)
        ? answers[question.question_id] || ""
        : "",
    }));
  };

  const validateBeforeSubmit = () => {
    const missingAnswers = applicableQuestions
      .filter(
        (question) =>
          question.required &&
          !String(answers[question.question_id] || "").trim(),
      )
      .map((question) => question.question_id);
    const missingEvidence = applicableQuestions
      .filter(
        (question) =>
          question.evidence_required &&
          !(questionEvidence.get(question.question_id) || []).some(
            (item) =>
              item.verification_status === "Pending Review" ||
              item.verification_status === "Verified",
          ),
      )
      .map((question) => question.question_id);
    if (missingAnswers.length || missingEvidence.length) {
      setError(
        [
          missingAnswers.length
            ? `Required answers are missing for: ${missingAnswers.join(", ")}.`
            : "",
          missingEvidence.length
            ? `Required evidence is missing for: ${missingEvidence.join(", ")}.`
            : "",
        ]
          .filter(Boolean)
          .join(" "),
      );
      return false;
    }
    return true;
  };

  const saveProgress = async () => {
    setSaving(true);
    setError("");
    setFeedback("");
    try {
      const responses = responsePayload();
      const result = staffEntry
        ? await saveTPRMAssessmentVendorEntryApi(assessmentId, responses)
        : await saveVendorAssessmentResponsesApi(token, responses);
      setInvitation(result);
      setDirty(false);
      setFeedback(
        staffEntry
          ? "Responses saved on behalf of the vendor."
          : "Your progress has been saved.",
      );
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const submitAssessment = async () => {
    if (!validateBeforeSubmit()) return;
    setSaving(true);
    setError("");
    setFeedback("");
    try {
      if (staffEntry) {
        await saveTPRMAssessmentVendorEntryApi(assessmentId, responsePayload());
      } else {
        try {
          await saveVendorAssessmentResponsesApi(token, responsePayload());
        } catch (saveError) {
          if (saveError.response?.status !== 409) throw saveError;
        }
      }
      const result = staffEntry
        ? await submitTPRMAssessmentApi(assessmentId)
        : await submitVendorAssessmentApi(token);
      setInvitation((current) => ({
        ...current,
        status: result.status,
        can_edit: false,
      }));
      setDirty(false);
      setFeedback(
        staffEntry
          ? "The vendor assessment has been submitted for review."
          : result.already_submitted
            ? "Your assessment was already submitted for review."
            : "Your assessment has been submitted for review.",
      );
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const uploadEvidence = async (questionId, file) => {
    if (!file) return;
    setUploadingQuestion(questionId);
    setError("");
    setFeedback("");
    try {
      const result = staffEntry
        ? await uploadTPRMAssessmentEvidenceApi({
            assessmentId,
            vendorId: invitation.vendor_id,
            questionId,
            file,
          })
        : await uploadVendorAssessmentEvidenceApi(token, questionId, file);
      const uploadedEvidence = {
          ...result,
          filename: result.filename || result.original_filename,
      };
      setInvitation((current) => ({
          ...current,
          evidence: [...(current?.evidence || []), uploadedEvidence],
      }));
      const question = invitation.questions.find(
        (item) => item.question_id === questionId,
      );
      if (question?.question_type === "evidence") {
        updateAnswer(questionId, "Evidence uploaded");
      } else {
        setDirty(true);
      }
      setFeedback(`Evidence "${uploadedEvidence.filename}" was uploaded.`);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setUploadingQuestion("");
    }
  };

  const renderAnswer = (question) => {
    const value = answers[question.question_id] || "";
    const disabled = !invitation.can_edit || saving;
    if (question.question_type === "yes_no") {
      return (
        <select
          className={inputClass}
          aria-label={`Answer ${question.question_text}`}
          disabled={disabled}
          value={value}
          onChange={(event) => updateAnswer(question.question_id, event.target.value)}
        >
          <option value="">Select an answer</option>
          {["Yes", "No"].map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
    if (question.question_type === "yes_no_partial") {
      return (
        <select
          className={inputClass}
          aria-label={`Answer ${question.question_text}`}
          disabled={disabled}
          value={value}
          onChange={(event) => updateAnswer(question.question_id, event.target.value)}
        >
          <option value="">Select an answer</option>
          {["Yes", "No", "Partial"].map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
    if (question.question_type === "single_choice") {
      return (
        <select
          className={inputClass}
          aria-label={`Answer ${question.question_text}`}
          disabled={disabled}
          value={value}
          onChange={(event) => updateAnswer(question.question_id, event.target.value)}
        >
          <option value="">Select an answer</option>
          {question.allowed_answers.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
    if (question.question_type === "multiple_choice") {
      const selected = value ? value.split(",").map((item) => item.trim()) : [];
      return (
        <fieldset className="mt-3 space-y-2">
          <legend className="sr-only">{question.question_text} choices</legend>
          {question.allowed_answers.map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                disabled={disabled}
                checked={selected.includes(option)}
                onChange={(event) => {
                  const next = event.target.checked
                    ? [...selected, option]
                    : selected.filter((item) => item !== option);
                  updateAnswer(question.question_id, next.join(", "));
                }}
              />
              {option}
            </label>
          ))}
        </fieldset>
      );
    }
    if (question.question_type === "number") {
      return (
        <input
          className={inputClass}
          aria-label={`Answer ${question.question_text}`}
          type="number"
          disabled={disabled}
          value={value}
          onChange={(event) => updateAnswer(question.question_id, event.target.value)}
        />
      );
    }
    if (question.question_type === "date") {
      return (
        <input
          className={inputClass}
          aria-label={`Answer ${question.question_text}`}
          type="date"
          disabled={disabled}
          value={value}
          onChange={(event) => updateAnswer(question.question_id, event.target.value)}
        />
      );
    }
    if (question.question_type === "text" || question.question_type === "evidence") {
      return (
        <textarea
          className={inputClass}
          aria-label={`Answer ${question.question_text}`}
          rows={3}
          disabled={disabled}
          value={value}
          onChange={(event) => updateAnswer(question.question_id, event.target.value)}
        />
      );
    }
    return (
      <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">
        This questionnaire contains an unsupported answer format.
      </p>
    );
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-12 dark:bg-slate-950">
        <p className="mx-auto max-w-3xl text-center text-slate-700 dark:text-slate-200" role="status">
          Loading assessment...
        </p>
      </main>
    );
  }

  if (error && !invitation) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-12 dark:bg-slate-950">
        <section className="mx-auto max-w-xl rounded-xl border border-rose-200 bg-white p-6 dark:border-rose-900 dark:bg-slate-900">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
            {staffEntry ? "Assessment unavailable" : "Assessment link unavailable"}
          </h1>
          <p className="mt-3 text-sm text-rose-700 dark:text-rose-300" role="alert">{error}</p>
          {staffEntry ? (
            <button
              className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200"
              onClick={onBack}
              type="button"
            >
              Back to assessments
            </button>
          ) : token && (
            <button
              className="mt-4 rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
              onClick={() => setReloadKey((current) => current + 1)}
              type="button"
            >
              Retry
            </button>
          )}
        </section>
      </main>
    );
  }

  const editable = Boolean(invitation?.can_edit);
  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 dark:bg-slate-950 sm:px-6">
      <section className="mx-auto max-w-4xl space-y-5">
        <header className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          {staffEntry && (
            <button
              className="mb-4 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              onClick={onBack}
              type="button"
            >
              Back to assessments
            </button>
          )}
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
            {staffEntry ? "Vendor response entry (admin)" : "Third-party assessment"}
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
            {invitation.questionnaire_name}
          </h1>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Vendor</dt>
              <dd className="font-medium text-slate-900 dark:text-white">{invitation.vendor_name}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Assessment reference</dt>
              <dd className="font-medium text-slate-900 dark:text-white">{invitation.assessment_id}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Due date</dt>
              <dd className="font-medium text-slate-900 dark:text-white">{formatDate(invitation.due_date)}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Status</dt>
              <dd className="font-medium text-slate-900 dark:text-white">{invitation.status}</dd>
            </div>
          </dl>
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">{invitation.instructions}</p>
        </header>

        {error && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200" role="alert">
            {error}
          </p>
        )}
        {feedback && (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" role="status">
            {feedback}
          </p>
        )}

        <div className="space-y-4">
          <section
            aria-label="Required response completion"
            className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-slate-900 dark:text-white">
                Required questions
              </span>
              <span className="text-slate-600 dark:text-slate-300">
                {completedRequiredQuestions.length} of {requiredQuestions.length} complete
              </span>
            </div>
            <progress
              aria-label="Required question completion"
              className="mt-2 h-2 w-full accent-blue-700"
              max="100"
              value={completionPercentage}
            />
            <div className="mt-2 flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-600 dark:text-slate-300">
                {completionPercentage}% complete
              </span>
              {editable && dirty && (
                <span className="font-medium text-amber-700 dark:text-amber-300" role="status">
                  Unsaved changes
                </span>
              )}
            </div>
            {editable && unansweredRequiredQuestions.length > 0 && (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-200">
                {unansweredRequiredQuestions.map((question) => (
                  <li key={question.question_id}>
                    {question.question_text || question.question_id}
                    {question.evidence_required
                      && !(questionEvidence.get(question.question_id) || []).some(
                        (item) =>
                          item.verification_status === "Pending Review"
                          || item.verification_status === "Verified",
                      )
                      ? " (evidence required)"
                      : ""}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {applicableQuestions.map((question, index) => (
            <article
              key={question.question_id}
              className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
            >
              <h2 className="font-medium text-slate-900 dark:text-white">
                {index + 1}. {question.question_text}
                {question.required && <span className="ml-1 text-rose-600" aria-label="required">*</span>}
              </h2>
              {renderAnswer(question)}
              {question.evidence_required && (
                <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-800">
                  <label className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    Supporting evidence (required)
                    <input
                      className="mt-2 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-blue-700 disabled:opacity-60 dark:text-slate-300 dark:file:bg-slate-800 dark:file:text-blue-200"
                      type="file"
                      accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg"
                      disabled={!editable || uploadingQuestion === question.question_id}
                      onChange={(event) => uploadEvidence(question.question_id, event.target.files?.[0])}
                    />
                  </label>
                  {uploadingQuestion === question.question_id && (
                    <p className="mt-2 text-xs text-slate-500" role="status">Uploading evidence...</p>
                  )}
                  {(questionEvidence.get(question.question_id) || []).map((item) => (
                    <p key={item.evidence_id} className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                      {item.filename} — {item.verification_status}
                    </p>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>

        {editable ? (
          <div className="flex flex-wrap justify-end gap-3 pb-8">
            <button
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200"
              disabled={saving || Boolean(uploadingQuestion)}
              onClick={saveProgress}
              type="button"
            >
              {saving ? "Saving..." : "Save progress"}
            </button>
            <button
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
              disabled={saving || Boolean(uploadingQuestion)}
              onClick={submitAssessment}
              type="button"
            >
              {saving ? "Submitting..." : "Save and submit"}
            </button>
          </div>
        ) : (
          <p className="rounded-lg bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-950/40 dark:text-blue-200" role="status">
            This assessment is read-only because it has been submitted or is no longer editable.
          </p>
        )}
      </section>
    </main>
  );
};

export default VendorAssessmentPage;
